import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { MessageCircle, Printer } from "lucide-react";
import { api } from "@/lib/api";
import { inr, fmtDate, titleCase, waNumber } from "@/lib/format";
import { Button } from "@/components/ui/button";

export default function PartyLedger() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/parties/${id}/ledger`).then(({ data }) => setData(data));
  }, [id]);

  if (!data) return <div className="text-sm text-slate-500">Loading…</div>;
  const p = data.party;

  const whatsapp = () => {
    const msg = `Hi ${p.display_name}, your account statement with us shows an outstanding balance of ${inr(
      data.closing_balance
    )} as on ${fmtDate(new Date().toISOString().slice(0, 10))}. Please share your payment plan. Thank you!`;
    window.open(`https://wa.me/${waNumber(p.phone)}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  return (
    <div className="space-y-5" data-testid="party-ledger-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight" data-testid="ledger-party-name">
            {p.display_name}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {p.gstin ? <span className="font-mono">{p.gstin}</span> : "Unregistered"} · {p.state_name} ·{" "}
            <span className="font-mono">{p.phone}</span>
          </p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" data-testid="ledger-whatsapp" onClick={whatsapp} className="gap-1.5">
            <MessageCircle size={15} /> Share statement
          </Button>
          <Button size="sm" variant="outline" data-testid="ledger-print" onClick={() => window.print()} className="gap-1.5">
            <Printer size={15} /> Print
          </Button>
          <Button size="sm" data-testid="ledger-new-invoice" onClick={() => navigate(`/invoices/new?party_id=${p.id}`)}
            className="bg-blue-600 hover:bg-blue-700">
            New invoice
          </Button>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <Stat label="Closing balance" value={inr(data.closing_balance)} testid="ledger-closing-balance" accent />
        <Stat label="Entries" value={data.entries.length} testid="ledger-entry-count" />
        <Stat label="Credit period" value={`${p.credit_period_days} days`} testid="ledger-credit-days" />
      </div>

      <div className="rounded-md border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="ledger-table">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-2.5 text-left font-semibold">Date</th>
              <th className="px-3 py-2.5 text-left font-semibold">Particulars</th>
              <th className="px-3 py-2.5 text-left font-semibold">Reference</th>
              <th className="px-3 py-2.5 text-right font-semibold">Debit</th>
              <th className="px-3 py-2.5 text-right font-semibold">Credit</th>
              <th className="px-4 py-2.5 text-right font-semibold">Balance</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {data.entries.map((e, i) => (
              <tr key={i} className={`hover:bg-slate-50 transition-colors duration-150 ${e.document_id ? "cursor-pointer" : ""}`}
                onClick={() => e.document_id && navigate(`/invoices/${e.document_id}`)}
                data-testid={`ledger-row-${i}`}>
                <td className="px-4 py-2.5 text-xs text-slate-600 whitespace-nowrap">{fmtDate(e.date)}</td>
                <td className="px-3 py-2.5">{e.particulars}</td>
                <td className="px-3 py-2.5 font-mono text-xs text-slate-600">{e.reference || "—"}</td>
                <td className="px-3 py-2.5 text-right font-mono tabular-nums">{e.debit ? inr(e.debit) : "—"}</td>
                <td className="px-3 py-2.5 text-right font-mono tabular-nums">{e.credit ? inr(e.credit) : "—"}</td>
                <td className="px-4 py-2.5 text-right font-mono tabular-nums font-semibold">{inr(e.balance)}</td>
              </tr>
            ))}
            {!data.entries.length && (
              <tr><td colSpan={6} className="px-4 py-14 text-center text-sm text-slate-500">No transactions yet</td></tr>
            )}
          </tbody>
          <tfoot className="bg-slate-900 text-white">
            <tr>
              <td colSpan={5} className="px-4 py-3 text-xs uppercase tracking-widest font-semibold">Closing balance</td>
              <td className="px-4 py-3 text-right font-mono font-bold">{inr(data.closing_balance)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

const Stat = ({ label, value, testid, accent }) => (
  <div className={`rounded-md border p-4 ${accent ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white"}`}>
    <div className={`text-[11px] uppercase tracking-widest font-semibold ${accent ? "text-blue-400" : "text-slate-500"}`}>{label}</div>
    <div className="mt-2 font-mono text-xl font-bold tabular-nums" data-testid={testid}>{value}</div>
  </div>
);
